/**
 * AdminRedirectsPage — Manage 301/302 URL redirects.
 *
 * /admin/redirects
 *   - Critical for SEO when slugs/URLs change (e.g. /yaz-firsatlari → /p/yaz-2026)
 *   - List + create + edit + delete
 *   - Hit counter shows how many users were redirected
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Edit3, ArrowRight, ExternalLink, MoveRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";

const INPUT = "w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm";

export default function AdminRedirectsPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        load();
    }, [user, authLoading]); // eslint-disable-line

    const load = async () => {
        setLoading(true);
        try { const r = await api.get("/admin/redirects"); setItems(r.data || []); }
        finally { setLoading(false); }
    };

    const handleSave = async (data) => {
        if (data.id) await api.put(`/admin/redirects/${data.id}`, data);
        else await api.post("/admin/redirects", data);
        setEditing(null);
        load();
    };
    const handleDelete = async (id) => {
        if (!window.confirm("Bu yönlendirmeyi silmek istediğinize emin misiniz?")) return;
        await api.delete(`/admin/redirects/${id}`);
        load();
    };

    return (
        <AdminLayout
            title="301 / 302 Yönlendirmeleri"
            action={
                <button data-testid="new-redirect-btn" onClick={() => setEditing({})} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold">
                    <Plus className="h-4 w-4" /> Yeni Yönlendirme
                </button>
            }
        >
            <div className="mb-4 p-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 text-xs text-slate-600 dark:text-slate-300">
                <strong className="text-sky-700 dark:text-sky-300">SEO İpucu:</strong> URL'lerinizi değiştirdiğinizde (örn. eski blog yazısı yeni CMS sayfasına taşındı), Google'dan gelen ziyaretçileri kaybetmemek için <strong>301 yönlendirme</strong> ekleyin. Geçici yönlendirme için <strong>302</strong> kullanın.
            </div>
            {loading ? <div>Yükleniyor...</div> : items.length === 0 ? (
                <div className="p-10 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-center">
                    <MoveRight className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500 text-sm mb-4">Henüz yönlendirme yok. URL'lerinizi taşıdığınızda Google sıralamanızı korumak için ekleyin.</p>
                    <button onClick={() => setEditing({})} className="px-5 py-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold">+ İlk Yönlendirme</button>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                    <table className="w-full text-sm" data-testid="redirects-table">
                        <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                            <tr>
                                <th className="px-4 py-3 text-left">Kaynak (eski)</th>
                                <th className="px-4 py-3"></th>
                                <th className="px-4 py-3 text-left">Hedef (yeni)</th>
                                <th className="px-4 py-3 text-center">Tip</th>
                                <th className="px-4 py-3 text-center">Tıklanma</th>
                                <th className="px-4 py-3 text-center">Durum</th>
                                <th className="px-4 py-3 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r) => (
                                <tr key={r.id} data-testid={`redirect-row-${r.id}`} className="border-t border-slate-100 dark:border-slate-800/50">
                                    <td className="px-4 py-3 font-mono text-xs text-slate-700 dark:text-slate-200">{r.from_path}</td>
                                    <td className="px-4 py-3 text-slate-400"><ArrowRight className="h-3.5 w-3.5 inline" /></td>
                                    <td className="px-4 py-3 font-mono text-xs">
                                        {r.to_path.startsWith("http") ? (
                                            <a href={r.to_path} target="_blank" rel="noreferrer" className="text-sky-600 hover:underline inline-flex items-center gap-1">{r.to_path} <ExternalLink className="h-3 w-3" /></a>
                                        ) : (
                                            <span className="text-slate-700 dark:text-slate-200">{r.to_path}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700">{r.status_code}</span></td>
                                    <td className="px-4 py-3 text-center text-slate-500">{r.hits || 0}</td>
                                    <td className="px-4 py-3 text-center">
                                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.enabled ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300" : "bg-slate-100 text-slate-500"}`}>{r.enabled ? "Aktif" : "Pasif"}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => setEditing(r)} className="p-1.5 rounded hover:bg-sky-50 dark:hover:bg-sky-950/40 text-sky-600"><Edit3 className="h-3.5 w-3.5" /></button>
                                        <button onClick={() => handleDelete(r.id)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && <RedirectEditor redirect={editing} onClose={() => setEditing(null)} onSave={handleSave} />}
        </AdminLayout>
    );
}

function RedirectEditor({ redirect, onClose, onSave }) {
    const [data, setData] = useState({
        from_path: redirect.from_path || "",
        to_path: redirect.to_path || "",
        status_code: redirect.status_code || 301,
        enabled: redirect.enabled ?? true,
        note: redirect.note || "",
        id: redirect.id,
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setData({ ...data, [k]: v });

    const save = async () => {
        setSaving(true);
        try { await onSave(data); }
        catch (e) { alert(e.response?.data?.detail || "Kayıt başarısız"); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
            <div className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                    <h3 className="font-display text-lg font-bold">{data.id ? "Yönlendirmeyi Düzenle" : "Yeni Yönlendirme"}</h3>
                    <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800">✕</button>
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Kaynak yol (eski URL)"><input data-testid="rd-from" value={data.from_path} onChange={(e) => set("from_path", e.target.value)} placeholder="/eski-blog-yazisi" className={`${INPUT} font-mono`} /></Field>
                    <Field label="Hedef yol (yeni URL)"><input data-testid="rd-to" value={data.to_path} onChange={(e) => set("to_path", e.target.value)} placeholder="/p/yeni-icerik veya https://..." className={`${INPUT} font-mono`} /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Durum kodu">
                            <select data-testid="rd-status" value={data.status_code} onChange={(e) => set("status_code", parseInt(e.target.value, 10))} className={INPUT}>
                                <option value={301}>301 (Kalıcı)</option>
                                <option value={302}>302 (Geçici)</option>
                            </select>
                        </Field>
                        <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!data.enabled} onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4 accent-sky-500" /><span className="text-sm font-semibold">Aktif</span></label>
                    </div>
                    <Field label="Not (admin'e gözükür, opsiyonel)"><input value={data.note} onChange={(e) => set("note", e.target.value)} placeholder="Sebep / context..." className={INPUT} /></Field>
                </div>
                <div className="p-5 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-xl bg-slate-200 dark:bg-slate-700 text-sm font-bold">İptal</button>
                    <button data-testid="rd-save" onClick={save} disabled={saving} className="px-5 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold disabled:opacity-50">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return <label className="block"><span className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>{children}</label>;
}
